<template>
  <div class="report-page">
    <div v-if="state === 'loading'" class="fullscreen-center">
      <div class="spinner"></div>
      <p class="loading-text">加载中...</p>
    </div>

    <div v-else-if="state === 'error'" class="fullscreen-center">
      <div class="status-icon error-icon">✕</div>
      <p class="status-title">报告不存在或链接无效</p>
      <p class="status-desc">请检查链接是否正确，或在小程序中重新获取</p>
    </div>

    <div v-else-if="state === 'expired'" class="fullscreen-center">
      <div class="status-icon expired-icon">⏰</div>
      <p class="status-title">报告已过期</p>
      <p class="status-desc">请在小程序中查看完整答题记录</p>
      <button
        v-if="showMiniProgramEntry"
        class="cta-btn"
        @click="handleOpenMiniProgram"
      >
        打开小程序查看 ▸
      </button>
    </div>

    <div v-else-if="state === 'normal'" class="report-content">
      <div class="hero-header">
        <div class="course-info">
          <span class="course-name">{{ report.course_name }}</span>
          <span class="separator">·</span>
          <span class="lesson-name">{{ report.lesson_name }}</span>
        </div>
        <div class="score-title">{{ reportDisplay.title }}</div>
        <div class="score-display">
          <span class="score-number" :class="`metric-${reportDisplay.type}`">
            {{ reportDisplay.mainValue }}
          </span>
          <span v-if="reportDisplay.suffix" class="score-total">{{ reportDisplay.suffix }}</span>
        </div>
        <div v-if="reportDisplay.subText" class="score-sub-text">
          {{ reportDisplay.subText }}
        </div>
        <div class="student-name">{{ report.student_name }}</div>
      </div>

      <div class="stats-section">
        <div class="stats-grid">
          <div
            v-for="item in reportDisplay.stats"
            :key="item.label"
            class="stat-card"
          >
            <div class="stat-value">{{ item.value }}</div>
            <div class="stat-label">{{ item.label }}</div>
          </div>
          <div class="stat-card" v-if="report.show_rank">
            <div class="stat-value rank-value">
              <span class="rank-num">{{ report.class_rank }}</span>
              <span class="rank-divider">/</span>
              <span class="rank-total">{{ report.class_total }}</span>
            </div>
            <div class="stat-label">班级排名</div>
          </div>
          <div class="stat-card rank-hidden" v-else>
            <div class="stat-value">—</div>
            <div class="stat-label">班级排名</div>
          </div>
        </div>
      </div>

      <div v-if="reportDisplay.showQuestionSummary" class="questions-section">
        <h3 class="section-title">答题概况</h3>
        <div class="questions-grid">
          <div
            v-for="q in (report.questions || [])"
            :key="q.seq"
            class="question-block"
            :class="q.is_correct ? 'correct' : 'wrong'"
          >
            <span class="q-seq">{{ q.seq }}</span>
          </div>
        </div>
        <div class="questions-legend">
          <span class="legend-item correct-legend">
            <span class="legend-dot correct-dot"></span>
            正确 {{ correctCount }}题
          </span>
          <span class="legend-item wrong-legend">
            <span class="legend-dot wrong-dot"></span>
            错误 {{ wrongCount }}题
          </span>
        </div>
      </div>
      <div v-else class="questions-section grade-summary">
        <h3 class="section-title">评价说明</h3>
        <p class="grade-summary-text">
          这是一份自定义作业评价，老师按等级给出结果，暂无题目对错统计。
        </p>
      </div>

      <div v-if="showMiniProgramEntry" class="cta-section">
        <button class="cta-btn" @click="handleOpenMiniProgram">
          打开小程序查看详细答题记录 ▸
        </button>
      </div>

      <div class="page-footer">
        <p>H5 页面展示简报，完整答题详情请在小程序中查看</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getH5Report } from '../api/report'
const { parseH5ReportResponse } = require('../utils/report-response')
const { buildMiniProgramUrl } = require('../utils/mini-program-url')
const { buildReportDisplay } = require('../utils/report-display')

export default {
  name: 'ReportPage',
  data() {
    return {
      state: 'loading',
      report: null,
    }
  },
  computed: {
    reportDisplay() {
      return buildReportDisplay(this.report || {})
    },
    correctCount() {
      if (!this.report || !this.report.questions) return 0
      return this.report.questions.filter(q => q.is_correct).length
    },
    wrongCount() {
      if (!this.report || !this.report.questions) return 0
      return this.report.questions.filter(q => !q.is_correct).length
    },
    showMiniProgramEntry() {
      return false
    },
  },
  mounted() {
    this.fetchReport()
  },
  methods: {
    async fetchReport() {
      const token = this.$route.params.token
      if (!token || token === 'invalid') {
        this.state = 'error'
        return
      }
      try {
        const res = await getH5Report(token)
        const parsed = parseH5ReportResponse(res)
        this.state = parsed.state
        this.report = parsed.report
      } catch (err) {
        this.state = 'error'
      }
    },
    handleOpenMiniProgram() {
      const url = buildMiniProgramUrl({
        env: process.env,
        report: this.report,
      })
      if (!url) {
        return
      }
      window.location.href = url
    },
  },
}
</script>

<style scoped>
.report-page {
  min-height: 100vh;
  background-color: #f5f6f7;
  font-size: 16px;
  line-height: 1.4;
}

.report-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  min-height: 100vh;
  margin: 0 auto;
  padding-bottom: 32px;
}

.fullscreen-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 480px;
  min-height: 100vh;
  margin: 0 auto;
  padding: 32px 20px;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top-color: #4a90d9;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  color: #999;
  font-size: 14px;
}

.status-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 20px;
}

.error-icon {
  background-color: #fde8e7;
  color: #d9534f;
}

.expired-icon {
  background-color: #fff4e5;
  color: #f0ad4e;
}

.status-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.status-desc {
  font-size: 14px;
  color: #777;
  line-height: 1.6;
  max-width: 288px;
}

.cta-btn {
  margin-top: 28px;
  background: linear-gradient(135deg, #4a90d9 0%, #6ba3e5 100%);
  color: white;
  font-size: 15px;
  font-weight: 500;
  padding: 13px 28px;
  border-radius: 999px;
  border: none;
  box-shadow: 0 4px 12px rgba(74, 144, 217, 0.25);
  cursor: pointer;
  line-height: 1.2;
}

.hero-header {
  background: linear-gradient(180deg, #4a90d9 0%, #6ba3e5 100%);
  color: white;
  padding: clamp(24px, 7vw, 32px) 20px clamp(28px, 8vw, 36px);
  text-align: center;
}

.course-info {
  font-size: 14px;
  opacity: 0.95;
  margin-bottom: 12px;
}

.score-title {
  font-size: 13px;
  opacity: 0.78;
  margin-bottom: 8px;
}

.separator {
  margin: 0 6px;
  opacity: 0.6;
}

.score-display {
  margin-bottom: 12px;
}

.score-number {
  font-size: clamp(44px, 14vw, 56px);
  font-weight: 700;
  line-height: 1;
}

.score-number.metric-accuracy,
.score-number.metric-grade {
  font-size: clamp(38px, 12vw, 48px);
}

.score-total {
  font-size: 16px;
  opacity: 0.9;
  margin-left: 4px;
}

.score-sub-text {
  font-size: 13px;
  opacity: 0.8;
  margin-top: 8px;
}

.student-name {
  font-size: 15px;
  opacity: 0.95;
  margin-top: 12px;
}

.stats-section {
  padding: 16px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 16px 8px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.stat-value {
  font-size: 17px;
  font-weight: 700;
  color: #222;
  margin-bottom: 5px;
}

.stat-unit {
  font-size: 12px;
  font-weight: 500;
  color: #666;
  margin-left: 2px;
}

.stat-label {
  font-size: 12px;
  color: #888;
}

.rank-value {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
}

.rank-num,
.rank-total {
  font-size: 17px;
  font-weight: 700;
}

.rank-divider {
  font-size: 14px;
  color: #999;
}

.questions-section {
  background: white;
  margin: 0 16px;
  padding: 20px 16px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin-bottom: 16px;
}

.questions-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.question-block {
  aspect-ratio: 1;
  min-width: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.question-block.correct {
  background-color: #e7f8ef;
  color: #1e9f62;
}

.question-block.wrong {
  background-color: #fdeaea;
  color: #d9534f;
}

.questions-legend {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #666;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.correct-dot {
  background-color: #1e9f62;
}

.wrong-dot {
  background-color: #d9534f;
}

.grade-summary {
  text-align: left;
}

.grade-summary-text {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.7;
}

.cta-section {
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding: 0 16px;
}

.page-footer {
  text-align: center;
  font-size: 12px;
  color: #999;
  margin-top: 24px;
  padding: 0 16px;
}

@media screen and (max-width: 360px) {
  .stats-grid {
    gap: 8px;
  }

  .stat-card {
    padding: 14px 6px;
  }

  .questions-grid {
    gap: 8px;
  }
}

@media screen and (min-width: 768px) {
  .report-page {
    padding: 24px 0;
  }

  .report-content,
  .fullscreen-center {
    min-height: calc(100vh - 48px);
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  }

  .fullscreen-center {
    background-color: #fff;
  }
}
</style>
